import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { useTranslation } from 'react-i18next';
import briksTheme from '@shared/briksTheme';
import {
  Check, HelpCircle, Mail, MessageSquare
} from '@huspy/briks-icons';
import { useRequestOtp } from '@modules/core/hooks/mutations/useRequestOtp';
import useGetOpportunityId from '@modules/core/hooks/useGetOpportunityId';
import { Tooltip } from '@huspy/forge';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { useAuthStore } from '@modules/core/store';
import { AUTH_METHOD, AuthMethod } from '@modules/core/api/const';

const OtpToolTip = ({ authMethod }: { authMethod: AuthMethod }) => {
  const { t } = useTranslation();
  return (
    <Tooltip
      label={ (
        <Flex direction='column' p='4' gap='2' maxW='350'>
          <Text color='text-primary' size='lg' fontWeight='medium'>
            {t('auth.requestOtp.toolTipTitle', {
              title: t(
                `auth.requestOtp.${authMethod === AUTH_METHOD.email ? 'emailLabel' : 'smsLabel'}`
              ),
            })}
          </Text>
          <Text color='text-tertiary' size='md'>
            {t('auth.requestOtp.toolTipDesc', {
              authMethod: t(
                `auth.requestOtp.${authMethod === AUTH_METHOD.email ? 'emailAddress' : 'phoneNumber'}`
              ),
            })}
          </Text>
        </Flex>
      ) }
      bg='white'
    >
      <Box>
        <HelpCircle
          height='20'
          width='20'
          color={ briksTheme.colors['neutral.500'] }
        />
      </Box>
    </Tooltip>
  );
};

type AuthMethodOptionProps = {
  icon: React.ReactNode;
  label: string;
  authMethod: AuthMethod;
  contactInfo: string;
  isSelected: boolean;
  onSelect: (authMethod: AuthMethod) => void;
};

const CheckIcon = ({ isSelected }: { isSelected: boolean }) => (
  <Flex
    justify='center'
    align='center'
    p='1'
    borderRadius='50%'
    border='1px solid'
    borderColor='border-primary'
    bg={ isSelected ? 'neutral.900' : 'neutral.1' }
    w='22px'
    h='22px'
  >
    {isSelected ? <Check color='white' /> : <></>}
  </Flex>
);

const AuthMethodOption = ({
  icon,
  label,
  authMethod,
  contactInfo,
  isSelected,
  onSelect,
}: AuthMethodOptionProps) => (
  <Button
    p='4'
    border='1px solid'
    borderColor='neutral.100'
    borderRadius='2'
    cursor='pointer'
    variant='outline'
    onClick={ () => onSelect(authMethod) }
  >
    <Flex justify='space-between' align='center' w='100%'>
      <Flex direction='row' align='center' gap='2'>
        {icon}
        <Text color='text-primary' fontWeight='medium' size='lg'>
          {label}
        </Text>
        <Text
          color='neutral.500'
          fontWeight='medium'
          maxW='150px'
          whiteSpace='nowrap'
          overflow='hidden'
          textOverflow='ellipsis'
        >
          {contactInfo}
        </Text>
        <OtpToolTip authMethod={ authMethod } />
      </Flex>
      <CheckIcon isSelected={ isSelected } />
    </Flex>
  </Button>
);

export const RequestOtp = ({
  maskedEmail,
  maskedMobile,
  setSession,
  setIsOtpRequested,
  setSelectedAuthMethod,
  selectedAuthMethod,
  isSmsAuthMethodAvailable,
}: {
  maskedEmail: string;
  maskedMobile: string;
  setSession: (session: string) => void;
  setIsOtpRequested: () => void;
  selectedAuthMethod: AuthMethod;
  setSelectedAuthMethod: (_authMethod: AuthMethod) => void;
  isSmsAuthMethodAvailable?: boolean;
}) => {
  const { t } = useTranslation();
  const oppId = useGetOpportunityId();
  const { mutateAsync, isPending } = useRequestOtp(oppId!);
  const { getClientId } = useAuthStore();

  const handleOnClick = async () => {
    setIsOtpRequested();
    trackAmplitudeEvent(
      USER_EVENTS.AUTH.VAULT_REQUEST_OTP_CLICKED,
      {
        client_id: getClientId(),
        method: selectedAuthMethod,
      }
    );
    const { session } = await mutateAsync(selectedAuthMethod);
    setSession(session);
  };

  return (
    <Flex
      border='1px solid'
      borderColor='neutral.100'
      borderRadius='4'
      p='8'
      gap='4'
      direction='column'
      shadow='shadow-4'
      width='100%'
      maxWidth='500'
    >
      <Flex direction='column' gap='2'>
        <Text size='5xl' fontWeight='medium'>
          {t('auth.requestOtp.welcome')}
        </Text>
        <Text size='lg' fontWeight='medium'>
          {t('auth.requestOtp.instructions')}
        </Text>
      </Flex>
      <AuthMethodOption
        icon={ <Mail /> }
        label={ t('auth.requestOtp.emailLabel') }
        authMethod={ AUTH_METHOD.email }
        contactInfo={ maskedEmail }
        isSelected={ selectedAuthMethod === AUTH_METHOD.email }
        onSelect={ setSelectedAuthMethod }
      />
      { isSmsAuthMethodAvailable
        && (
          <AuthMethodOption
            icon={ <MessageSquare /> }
            label={ t('auth.requestOtp.smsLabel') }
            authMethod={ AUTH_METHOD.sms }
            contactInfo={ maskedMobile }
            isSelected={ selectedAuthMethod === AUTH_METHOD.sms }
            onSelect={ setSelectedAuthMethod }
          />
        )}
      <Flex align='center' gap='1'>
        <Text size='md' color='neutral.500'>
          {t('auth.requestOtp.error')}
        </Text>
      </Flex>
      <Button
        data-testid='request-otp-btn'
        onClick={ handleOnClick }
        loading={ isPending }
        variant={ isPending ? 'secondary' : 'primary' }
      >
        {t('auth.requestOtp.sendButton')}
      </Button>
      <Flex justify='center' align='center' gap='1'>
        <Text color='neutral.500' size='sm'>
          {t('auth.poweredBy')}
        </Text>
        <Text color='neutral.500' fontWeight='semibold'>
          Client Vault
        </Text>
      </Flex>
    </Flex>
  );
};
